import { useState, type FormEvent } from 'react';
import {
  useCreateReference,
  useDeleteReference,
  useDomains,
  useReferences,
} from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Light reference manager — extension articles, manual PDFs (linked,
 * not stored — those go in Photos), regulatory docs.
 */
export default function References() {
  const q = useReferences();
  const create = useCreateReference();
  const del = useDeleteReference();
  const domainsQ = useDomains();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const domainName = (id: string | null) =>
    id ? (domainsQ.data ?? []).find((d) => d.id === id)?.name ?? id : null;

  return (
    <PullToRefresh
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ['references'] })}
    >
      <div className="space-y-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Library · References
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              References
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              External links, extension articles, regulatory pages — anything you'll want to find
              again.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="h-11 self-start sm:self-auto" data-testid="new-reference">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New reference
          </Button>
        </header>

        {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {q.data && q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No references yet.</p>
        ) : null}
        {q.data && q.data.length > 0 ? (
          <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="reference-list">
            {q.data.map((r) => (
              <li
                key={r.id}
                className="flex items-start gap-3 px-4 py-3"
                data-testid={`reference-row-${r.id}`}
              >
                <div className="flex-1 min-w-0 space-y-0.5">
                  {domainName(r.domainId) ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {domainName(r.domainId)}
                    </p>
                  ) : null}
                  <p className="font-display text-base font-medium leading-tight">{r.title}</p>
                  {r.url ? (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden /> Open
                    </a>
                  ) : null}
                  {r.notes ? <p className="text-sm text-muted-foreground pt-1">{r.notes}</p> : null}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => del.mutateAsync(r.id)}
                  aria-label="Delete reference"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <Sheet open={creating} onOpenChange={setCreating}>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle>New reference</SheetTitle>
            </SheetHeader>
            <ReferenceForm
              domains={domainsQ.data ?? []}
              onSubmit={async (body) => {
                await create.mutateAsync(body);
                setCreating(false);
              }}
              onCancel={() => setCreating(false)}
              pending={create.isPending}
            />
          </SheetContent>
        </Sheet>
      </div>
    </PullToRefresh>
  );
}

function ReferenceForm({
  domains,
  onSubmit,
  onCancel,
  pending,
}: {
  domains: Array<{ id: string; name: string }>;
  onSubmit: (body: {
    title: string;
    url: string | null;
    notes: string | null;
    domainId: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  pending: boolean;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [domainId, setDomainId] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title required.');
      return;
    }
    try {
      await onSubmit({
        title: title.trim(),
        url: url.trim() || null,
        notes: notes.trim() || null,
        domainId: domainId || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 pt-4 pb-2" data-testid="reference-form">
      <div className="space-y-1.5">
        <Label className="font-mono text-[11px] uppercase tracking-wider">Title *</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus className="h-11" data-testid="reference-title" />
      </div>
      <div className="space-y-1.5">
        <Label className="font-mono text-[11px] uppercase tracking-wider">URL</Label>
        <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label className="font-mono text-[11px] uppercase tracking-wider">Domain</Label>
        <select
          value={domainId}
          onChange={(e) => setDomainId(e.target.value)}
          className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">No domain</option>
          {domains.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1.5">
        <Label className="font-mono text-[11px] uppercase tracking-wider">Notes</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
        />
      </div>
      {error ? <p className="text-sm text-warning">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending} data-testid="reference-submit">
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
